import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';

import {of, forkJoin, Observable} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {NotificationType} from '~core/enum';
import {ModalService} from '~shared/components/elements/global-modal/modal.service';
import {SpinnerService} from '~shared/components/buttons/spinner/spinner.service';
import {ButtonsTranslations} from '~shared/components/buttons/buttons-translations.service';
import {FormLabelTranslationService} from '~shared/components/form-fields/translations/form-label-translations.service';
import {
  AttributeService,
  DefaultNotificationService,
  PriceCategoryService,
  PriceReductionService, TixxSelectService,
  TixxSelectSubmissionService
} from '~core/services';
import {Attribute, Club, Lottery, LotteryExecutionSummary, PriceCategory, PriceReduction} from '~core/models';

/**
 * Component that displays the filter requests step of the execution process
 */
@UntilDestroy()
@Component({
  selector: 'app-filter-requests',
  templateUrl: './filter-requests.component.html',
  styleUrls: ['../../../lottery-execution.component.scss']
})

export class FilterRequestsComponent implements OnInit {
  @Input() public selectedLottery: Lottery;
  @Input() public selectedClub: Club;
  @Input() public customQueries: Array<any>;
  @Input() public executionSummary: LotteryExecutionSummary;
  @Output() public openResultsTableEmitter: EventEmitter<any> = new EventEmitter(null);
  @Output() public executionSummaryEmitter: EventEmitter<LotteryExecutionSummary> = new EventEmitter(null);
  @Output() public iterationSubmissionFiltersEmitter: EventEmitter<LotteryExecutionSummary> = new EventEmitter(null);
  @Output() public nextStepEmitter: EventEmitter<any> = new EventEmitter(null);
  @Output() public savedFilteredFormEmitter: EventEmitter<UntypedFormGroup> = new EventEmitter(null);
  @Output() public savedDeviationFormEmitter: EventEmitter<any> = new EventEmitter(null);

  public loading: boolean;
  public multiselectDataAttributes: Array<any> = [];
  public multiselectDataReductions: Array<any> = [];
  public specificTicketCount: boolean = false;
  public spinnerName: string = 'summaryLoading';
  public lotteryExecutionForm: UntypedFormGroup;
  public priceDeviationForm: UntypedFormGroup;
  public priceDeviationCategories: Array<PriceCategory> = [];

  public clubAttributes: Array<Attribute>;
  public priceReductionsList: Array<PriceReduction>;
  public priceCategoriesList: Array<PriceCategory>;

  public i18nContinueTitle: string;
  public i18nContinueParagraph: string;
  public i18nFailedToRequest: string;

  constructor(
    protected attributeService: AttributeService,
    private formBuilder: UntypedFormBuilder,
    private priceReductionService: PriceReductionService,
    private priceCategoryService: PriceCategoryService,
    private submissionService: TixxSelectSubmissionService,
    public spinnerService: SpinnerService,
    public buttonsTranslations: ButtonsTranslations,
    public flTranslations: FormLabelTranslationService,
    private modalService: ModalService,
    private notificationService: DefaultNotificationService,
    public tixxSelectService: TixxSelectService
  ) {
    this.attributeService.castClubAttributes.pipe(untilDestroyed(this))
      .subscribe((data: Array<Attribute>) => {
        if (data) {
          this.clubAttributes = data;
          this.fillSelectDataAttributes();
        }
      });
    this.priceReductionService.castCurrentPriceReductionList.pipe(untilDestroyed(this))
      .subscribe(data => {
        if (data) {
          this.priceReductionsList = data;
          this.fillSelectDataReductions();
        }
      });
    this.priceCategoryService.castCurrentPriceCategoriesList.pipe(untilDestroyed(this))
      .subscribe(data => this.priceCategoriesList = data);
  }

  ngOnInit(): void {
    this.setUp();
    this.fillLotteryForm();
    this.i18nContinueTitle = $localize`:@@tixxSelect.modalTitle.continueModal:Confirm non-contiguous seating option?`;
    this.i18nContinueParagraph = $localize`:@@tixxSelect.modalParagraph.continueModal:This selected option will cause the upcoming seating process via Tixx to search for non-contiguous seats only, for all filtered requests. Would you like to continue?`;
    this.i18nFailedToRequest = $localize`:@@tixxSelect.alert.failedToRequest:Failed to request information from Tixx!`;
  }

  /**
   * Create the lottery form.
   */
  public fillLotteryForm(): void {
    this.lotteryExecutionForm = this.formBuilder.group({
      lotteryId: [this.selectedLottery.id],
      priceCategoryId: ['', [Validators.required]],
      ticketReductions: [''],
      ticketRequestType: ['primary', [Validators.required]],
      ticketCount: [0],
      postalCodeRangeFrom: [null, [Validators.maxLength(5), Validators.minLength(5)]],
      postalCodeRangeTo: [null, [Validators.maxLength(5), Validators.minLength(5)]],
      city: [''],
      attributes: [''],
      activeMembers: [false],
      submissionEndDate: [null],
      submissionStartDate: [null],
      contiguousSeating: [false],
      specialRequirement: [false],
      priceDeviationAllowed: [false],
      deviationsMapping: [null],
      negativeLuckBalance: [false]
    });
  }

  /**
   * Sets up the deviation part of the form.
   */
  public setDeviation(): void {
    const categories = this.formBuilder.array(this.getCategoriesGroup());

    this.priceDeviationForm = this.formBuilder.group({
      categories
    });
  }

  public getCategoriesGroup() {
    const categoriesArray = [];
    for (const category of this.priceDeviationCategories) {
      const reductionsArray = [];
      for (const reduction of category.assignedReductions) {
        reductionsArray.push({
          from: reduction.id,
          to: category.assignedReductions[0].id
        });
      }
      categoriesArray.push({
        id: category.id,
        capacity: category.capacity,
        assignedReductions: reductionsArray,
      });
    }
    return categoriesArray;
  }

  // eslint-disable-next-line complexity
  public formSubmit(form: UntypedFormGroup, jumpNext = false): any {
    if (form.valid) {
      this.priceDeviationCategories.length = 0;
      const formValue = {...form.value};
      this.spinnerService.show(this.spinnerName);
      this.customQueries.length = 0;

      if (!this.specificTicketCount) {
        delete formValue['ticketCount'];
      }
      delete formValue['deviationsMapping'];
      this.tixxSelectService.savedFilters.set({...formValue});
      formValue['deviationsMapping'] = form.value['deviationsMapping'];

      for (const prop in formValue) {
        if (!formValue[prop] || (Array.isArray(formValue[prop]) && !formValue[prop]?.length)) {
          delete formValue[prop];
        }
      }

      if (formValue['contiguousSeating']) {
        formValue['contiguousSeating'] = false;
      }
      if (formValue['priceDeviationAllowed']) {
        form.controls['deviationsMapping'].setValue(null);
        delete formValue['priceDeviationAllowed'];
      }

      if (formValue.submissionStartDate) {
        formValue.submissionStartDate = new Date(formValue.submissionStartDate);
      }
      if (formValue.submissionEndDate) {
        formValue.submissionEndDate = new Date(formValue.submissionEndDate);
      }

      this.submissionService.postIterationFilters(formValue)
        .pipe(untilDestroyed(this))
        .subscribe({
          next: (res: any) => {
            this.customQueries.push({iterationSubmissionFilters: res.id});

            this.submissionService.getExecutionSubmissionSummary(this.selectedLottery.id, this.customQueries)
              .pipe(untilDestroyed(this))
              .subscribe({
                next: (data: LotteryExecutionSummary) => {
                  this.savedFilteredFormEmitter.emit(formValue);
                  this.executionSummaryEmitter.emit(data);
                  this.iterationSubmissionFiltersEmitter.emit(res.id);

                  if (data.selectedPriceCategories && this.lotteryExecutionForm.value.priceDeviationAllowed) {
                    for (const category of this.priceCategoriesList) {
                      const exists = data.selectedPriceCategories.some(el => el === category.id);
                      if (exists) {
                        this.priceDeviationCategories.push(category);
                      }
                    }
                    this.setDeviation();
                  }
                  this.spinnerService.hide(this.spinnerName);

                  if (jumpNext) {
                    this.nextStepEmitter.emit('click');
                  }
                },
                error: (error => {
                  this.notificationService.showNotification({
                    type: NotificationType.ALERT,
                    message: this.i18nFailedToRequest
                  });
                  this.spinnerService.hide(this.spinnerName);
                })
              });
          },
          error: (error => {
            this.notificationService.showNotification({
              type: NotificationType.ALERT,
              message: this.i18nFailedToRequest
            });
            this.spinnerService.hide(this.spinnerName);
          })
        });
    }
  }

  /**
   * Adds the columns and keys for the table
   */
  protected setUp(): void {
    const calls: Array<undefined | Observable<any>> = [];
    if (!this.priceReductionsList) {
      calls.push(this.priceReductionService.get(this.selectedLottery).pipe(untilDestroyed(this), catchError(error => of(null))));
    }
    if (!this.priceCategoriesList) {
      calls.push(this.priceCategoryService.get(this.selectedLottery).pipe(untilDestroyed(this), catchError(error => of(null))));
    }
    if (!this.clubAttributes) {
      calls.push(this.attributeService.getClubAttribute(this.selectedClub.id).pipe(untilDestroyed(this), catchError(error => of(null))));
    }

    if (calls.length) {
      this.loading = true;
    }

    forkJoin(calls).subscribe((results: any) => {
      for (const data of results) {
        if (data[0] instanceof PriceReduction) {
          this.priceReductionService.setCurrentPriceReductionList(data);
        } else if (data[0] instanceof PriceCategory) {
          this.priceCategoryService.setCurrentPriceCategoriesList(data);
        } else if (data[0] instanceof Attribute) {
          this.attributeService.announceClubAttributes(data);
        }
      }
      this.loading = false;
    });
  }

  /**
   * Fill the data for select form field.
   */
  public fillSelectDataAttributes(status = false): void {
    const data = this.clubAttributes;
    for (const att of data) {
      const obj = {id: att.id, name: att.name};
      this.multiselectDataAttributes.push(obj);
    }
  }

  /**
   * Fill the data for select form field.
   */
  public fillSelectDataReductions(): void {
    const data = this.priceReductionsList;
    for (const red of data) {
      const obj = {id: red.id, name: red.name};
      this.multiselectDataReductions.push(obj);
    }
  }

  public openResultsTable(): void {
    this.openResultsTableEmitter.emit('click');
  }

  public reset(): void {
    this.lotteryExecutionForm.reset({ticketRequestType: 'primary', ticketCount: 0, lotteryId: this.selectedLottery.id});
    this.executionSummaryEmitter.emit(undefined);
    this.specificTicketCount = false;
  }

  public nextStep(): void {
    if (this.lotteryExecutionForm.value.priceDeviationAllowed) {
      let arr = [];
      const obj = {};
      this.priceDeviationForm.value.categories.forEach(el => {
        arr = [...arr, ...el.assignedReductions];
      });
      for (const reduction of arr) {
        obj[reduction.from] = reduction.to;
      }
      this.lotteryExecutionForm.controls['deviationsMapping'].setValue(obj);
      this.formSubmit(this.lotteryExecutionForm, true);
    } else {
      this.nextStepEmitter.emit('next');
    }
  }

  public priceDeviationToggle(value: boolean) {
    if (value) {
      this.lotteryExecutionForm.addControl('priceDeviation', new UntypedFormControl('higher'));
    } else {
      this.lotteryExecutionForm.removeControl('priceDeviation');
    }
  }

  public getCategory(element) {
    const category = this.priceCategoriesList.find(el => el.id === element.value);
    return category.assignedReductions;
  }

  public updateFormReductions(value, cIndex, rIndex) {
    this.priceDeviationForm.value.categories[cIndex].assignedReductions[rIndex].to = value;
  }

  public updateFormCategory(value, index) {
    const category = this.priceCategoriesList.find(el => el.id === value);

    this.priceDeviationForm.value.categories[index].capacity = category.capacity;
    this.priceDeviationForm.value.categories[index].assignedReductions.forEach(el => {
      el.to = category.assignedReductions[0].id;
    });
  }

  get atLeastOne(): boolean {
    if (this.priceDeviationCategories.length) {
      const noEmptySeats = this.priceDeviationForm.value.categories.every(el => el.capacity > 0);
      return noEmptySeats;
    } else {
      if (this.executionSummary.numberOfRequestedTickets > 0
        && this.executionSummary.numberOfRequests > 0
        && this.executionSummary.priceCategoryCapacity > 0) {
        return true;
      } else {
        return false;
      }
    }
  }

  public async confirmContiguousSeating(val: boolean): Promise<void> {
    const seating = this.lotteryExecutionForm.controls['contiguousSeating'];
    if (val) {
      seating.setValue(false);
      const newVal = await this.showContinueModal();
      this.modalService.setStatus(false);
      seating.setValue(newVal);
    }
  }

  public async showContinueModal(): Promise<any> {
    this.modalService.setModalOptions(this.i18nContinueTitle, this.i18nContinueParagraph, this.buttonsTranslations.i18nContinue);
    const promise = await new Promise<boolean>((resolve, reject) => this.modalService.openModal(resolve, reject));
    if (promise) {
      return promise;
    } else {
      return false;
    }
  }

  public checkPostCodeFrom(valFrom: number): void {
    const valTo = this.lotteryExecutionForm.controls['postalCodeRangeTo'].value;

    if (valFrom > valTo) {
      this.lotteryExecutionForm.controls['postalCodeRangeTo'].setValue(valFrom);
    }
  }

  public checkPostCodeTo(valTo: number): void {
    const valFrom = this.lotteryExecutionForm.controls['postalCodeRangeFrom'].value;

    if (valFrom > valTo) {
      this.lotteryExecutionForm.controls['postalCodeRangeFrom'].setValue(valTo);
    }
  }

  public applyLastUsedFilters() {
    this.priceDeviationToggle(this.tixxSelectService.savedFilters()?.priceDeviationAllowed);
    this.lotteryExecutionForm.patchValue(this.tixxSelectService.savedFilters());
    this.specificTicketCount = this.tixxSelectService.savedFilters().ticketCount ?? false;
    for (const attribute of this.tixxSelectService.savedFilters()?.attributes || []) {
      const index = this.multiselectDataAttributes.findIndex(el => el.text === attribute);
      if (index !== undefined) {
        this.multiselectDataAttributes[index].selected = true;
      }
    }
    for (const reduction of this.tixxSelectService.savedFilters()?.ticketReductions || []) {
      const index = this.multiselectDataReductions.findIndex(el => el.id === reduction);
      if (index !== undefined) {
        this.multiselectDataReductions[index].selected = true;
      }
    }
    this.loading = true;
    setTimeout(() => {
      this.loading = false;
    }, 200);
  }
}
